/**
 * Phase 57 Plan 57-04 — PORTAL-04
 *
 * Thin wrapper around `useCreateExternalTask` that adds confirmation
 * navigation on success. This preserves the Phase 51 create→triage
 * behaviour (external creates always route to Triage per UAT #3).
 *
 * Quick task 260809-ofp (D-03) — the portal New-request dropzone used to
 * stage files and throw them away: `mutation.mutate` never sent them
 * anywhere. This hook now owns the upload phase too: after the task create
 * resolves, it walks any staged `files` sequentially, uploading each via
 * `uploadTaskAttachment(task.id, file)`, and calls `onCreated` with BOTH the
 * task id AND the per-file outcomes ONLY once every upload has settled — so
 * the caller never sees a "created" signal mid-upload. Per finding 7, the
 * API client already carries the external session + `X-Audience: external`
 * header for `/external/` paths, so no transport code is added here.
 */
import { useState } from 'react';
import { useCreateExternalTask } from './useCreateExternalTask.js';
import type { CreateExternalTaskVariables } from './useCreateExternalTask.js';
import { uploadTaskAttachment } from '../services/api/taskAttachments';
import { attachmentResultFromError, type AttachmentResult } from './useCreateComment';

export type { AttachmentResult };

export interface CreatePortalRequestVariables extends CreateExternalTaskVariables {
  /** Staged files from the dropzone — uploaded sequentially AFTER create resolves. */
  files?: File[];
}

export function useCreatePortalRequest(
  onCreated: (task: { id: number }, attachmentResults: AttachmentResult[]) => void,
) {
  const base = useCreateExternalTask();
  const [uploading, setUploading] = useState(false);

  async function uploadStagedFiles(taskId: number, files: File[]): Promise<AttachmentResult[]> {
    const results: AttachmentResult[] = [];
    for (const file of files) {
      try {
        await uploadTaskAttachment(taskId, file);
        results.push({ filename: file.name, status: 'ok' });
      } catch (err) {
        results.push(attachmentResultFromError(file.name, err));
      }
    }
    return results;
  }

  return {
    ...base,
    // The caller's spinner must cover the whole create-then-upload window,
    // not just the create round-trip.
    isPending: base.isPending || uploading,
    mutate: (vars: CreatePortalRequestVariables) => {
      const { files, ...createVars } = vars;
      base.mutate(createVars, {
        onSuccess: (task) => {
          if (!files || files.length === 0) {
            onCreated({ id: task.id }, []);
            return;
          }
          setUploading(true);
          void uploadStagedFiles(task.id, files).then((results) => {
            setUploading(false);
            onCreated({ id: task.id }, results);
          });
        },
      });
    },
  };
}
